import React, { useState, useEffect, useRef, useCallback, FC, useMemo, SyntheticEvent } from "react";
import { CloseOutlined, MenuOutlined } from "src/icons";
import { message, Button } from "antd";
import Shimmer from "src/shared/components/CustomisedShimmer";
import { useSlipper, useRedux } from "src/shared/hooks";
import {
  fetchNotes,
  addNotes,
  updateNotes,
  clearNotes,
  ClearTypes,
  FetchNotesPayload,
  AddNotesPayload,
  UpdateNotesPayload,
  INotesRootState,
} from "src/store/takeNotes";
import { Layout, Container } from "./styles";
import { rearrangeRowsinList, safeGet, dateUtility } from "src/shared/utils";
import { Notes, INote } from "src/shared/models/TakeNotesModel";
import { RouteComponentProps } from "react-router";
import { IRootState } from "src/shared/types";
import colors from "src/styles/variables.scss";
import ChartFallback from "src/shared/components/ChartFallback";

const TakeNotes: FC<RouteComponentProps> = (props) => {
  const [note, setNote] = useState<string>("");
  const [showButtonGroup, setShowButtonGroup] = useState(false);
  const [notesList, setNotesList] = useState<Notes[]>([]);
  const updatedList = useRef<INote>({
    id: 0,
    label: "",
    filters: "",
    performanceChartIds: [],
    currentChartId: 0,
    trendChartId: 0,
    pastChartId: 0,
    notes: [],
    formatedFilterData: "",
    bigNumber: "",
    progress: false,
    isPositive: true,
    vCompare: "",
    offset: 0,
    allChartsData: [],
    setChartsData: () => {},
    setNumbers: () => {},
  });

  const updateFlag = useRef<boolean>(false);

  type MappedStateProps = {
    notes: INotesRootState;
    selectedTab: string;
  };

  type MappedActionProps = {
    fetchNotes(payload: FetchNotesPayload);
    addNotes(payload: AddNotesPayload);
    updateNotes(payload: UpdateNotesPayload);
    clearNotes(type?: ClearTypes);
  };

  const mappedProps = useRedux<IRootState, MappedStateProps, MappedActionProps>(
    (state) => ({
      notes: state.notes,
      selectedTab: state.app.selectedTab,
    }),
    {
      fetchNotes,
      addNotes,
      updateNotes,
      clearNotes,
    }
  );

  //custom function provided to the useslipper
  const handleSlipperChange = useCallback(
    (initialIndex: number, finalIndex: number) => {
      const updatedArr = rearrangeRowsinList([...updatedList.current.notes], initialIndex, finalIndex);

      const updatechangedList = { ...updatedList.current, notes: updatedArr };
      setNotesList(rearrangeRowsinList(notesList, initialIndex, finalIndex));

      updatedList.current = updatechangedList;
      updateFlag.current = true;
    },
    [notesList, updateFlag]
  );

  const notesRef = useSlipper({
    handleSelector: "slider",
    onChange: handleSlipperChange,
  });

  //handler to changes text in the TextArea
  const notesChangedHandler = useCallback(
    (evt: SyntheticEvent, text: string): void => {
      evt.preventDefault();
      if (text.length <= 60) {
        setNote(text);
      }
    },
    [note]
  );

  useEffect(() => {
    if (note.length > 0) setShowButtonGroup(true);
    else if (note.length === 0) setShowButtonGroup(false);
  }, [note]);

  //add submit handler
  const onAddSubmitNotes = useCallback((): void => {
    mappedProps.addNotes({
      data: {
        text: note,
        snapshotDetail: updatedList.current.bigNumber,
      },
      userDetails: {
        pinId: safeGet(props, "match.params.pinID", -1),
      },
    });
  }, [note]);

  //update Submit handler
  const onUpdateSubmitNotes = useCallback((): void => {
    //update only if the changes was made to the updated list else just navigate back to home
    if (updateFlag.current) {
      mappedProps.updateNotes({
        data: updatedList.current,
        userDetails: {
          pinId: safeGet(props, "match.params.pinID", -1),
        },
        navigateOnSuccessfullUpdate: () => props.history.push("/"),
      });
    } else {
      props.history.push("/");
      mappedProps.clearNotes();
    }
  }, []);

  //deleting the row
  const onDeleteRow = useCallback(
    (evt, rowID) => {
      try {
        const noteList = notesList.filter((curr) => curr.id !== rowID);
        setNotesList(noteList);

        let newNotesList = { ...updatedList.current, notes: noteList };
        updatedList.current = newNotesList;

        //set update flag to true
        updateFlag.current = true;
        message.success("Deleted Successfully");
      } catch (err) {
        message.error("Error deleting notes. Please try again.");
      }
      evt.stopPropagation();
    },
    [notesList, updatedList, updateFlag]
  );

  //resetting & cleanUP text in text input after calling add Notes API
  useEffect(() => {
    if (mappedProps.notes.addedNotes.success) {
      setNote("");
      setShowButtonGroup(false);
      let newRow = safeGet(mappedProps, "notes.addedNotes.payload", null);
      if (newRow) {
        const formatedDate = dateUtility(newRow.updatedAt);
        const dateOut = {
          date: formatedDate.date,
          month: formatedDate.month,
        };
        newRow = { ...newRow, dateOut };
        const previousList = [...notesList];
        updatedList.current.notes.unshift(newRow);
        previousList.unshift(newRow);
        setNotesList(previousList);
      }
    }
  }, [mappedProps.notes.addedNotes.success]);

  /**
   * Initail render
   *  */
  useEffect(() => {
    //clearing the reducer
    mappedProps.clearNotes();
    //fetching the API
    mappedProps.fetchNotes({
      userDetails: {
        pinId: safeGet(props, "match.params.pinID", -1),
      },
      selectedTabValue: mappedProps.selectedTab,
    });
  }, []);

  const barsConfig = useMemo(
    () => [
      {
        height: "1.872rem",
        width: "4.16rem",
        repeatBar: 1,
      },
      { height: "1.24rem", width: "5.52rem", repeatBar: 1 },
    ],
    []
  );

  //setting the notesList ARRAY after fetching data
  useEffect(() => {
    if (mappedProps.notes.fetchedNotes.success) {
      if (mappedProps.notes.fetchedNotes.payload) {
        if (mappedProps.notes.fetchedNotes.payload.notes) setNotesList(mappedProps.notes.fetchedNotes.payload.notes);
        updatedList.current = mappedProps.notes.fetchedNotes.payload;
      }
    }
  }, [mappedProps.notes.fetchedNotes]);

  const renderNotesList = useCallback(() => {
    return notesList.map((curr, index) => (
      <li className="noteRow" key={curr.id + curr.updatedAt} id={`${index}`}>
        <div className="parallelRow">
          <div className="row">
            <div className="cross" onClick={(evt) => onDeleteRow(evt, curr.id)}>
              <CloseOutlined style={{ color: colors.milkyWhite, fontSize: "1.2rem" }} />
            </div>

            <div className="noteData">
              <span className="noteTitle">{curr.text}</span>
              <span className="noteTimestamp">
                {`${curr.dateOut.date} ${curr.dateOut.month} | ${curr.snapshotDetail}`}
              </span>
            </div>
            <div className="slider">
              <MenuOutlined style={{ color: colors.milkyWhite, fontSize: "1.2rem" }} />
            </div>
          </div>
          <div className="eachNoteSeparator" />
        </div>
      </li>
    ));
  }, [notesList]);

  const successful = safeGet(mappedProps, "notes.fetchedNotes.payload.successful", false);

  const onClickCancel = useCallback(
    (evt: React.MouseEvent) => {
      notesChangedHandler(evt, "");
      setShowButtonGroup(false);
    },
    [notesChangedHandler]
  );
  return (
    <>
      <Layout className="layout">
        <Container>
          <div className="notesTopSection">
            <div className="notesHeader">Take notes</div>
            <div className="navigationHome" onClick={onUpdateSubmitNotes}>
              DONE
            </div>
          </div>

          <div className="notesArea" />
          <div className="discountedHeadersGroup">
            <div className="dailyTotalDiscountedOrders">
              {safeGet(mappedProps, "notes.fetchedNotes.payload.label", "")}
            </div>
            <div className="daily">{safeGet(mappedProps, "notes.fetchedNotes.payload.formatedFilterData", "")}</div>
          </div>
          {!updatedList.current.bigNumber && <Shimmer className="ShimmerGroup" barsConfig={barsConfig} />}
          {updatedList.current.bigNumber && !successful && (
            <div className="progressGroup404">
              <ChartFallback />
            </div>
          )}

          {updatedList.current.bigNumber && successful && (
            <div className="progressGroup">
              <div className="discountedOrderNumber">{updatedList.current.bigNumber}</div>
              <div className="percentageGroup">
                <div
                  className={
                    updatedList.current.progress == updatedList.current.isPositive
                      ? "chart-progress-up"
                      : "chart-progress-down"
                  }
                >
                  {updatedList.current.vCompare === "" ? "" : !updatedList.current.progress ? "▼" : "▲"}
                </div>
                <div
                  className={
                    updatedList.current.vCompare === ""
                      ? "wowPercentage-neutral"
                      : updatedList.current.progress == updatedList.current.isPositive
                      ? "wowPercentage-up"
                      : "wowPercentage-down"
                  }
                >
                  {updatedList.current.vCompare === "" ? "- -" : updatedList.current.vCompare}
                </div>
              </div>
            </div>
          )}
          <div className="horzontalSeparator" />
          <div className="inputBoxNotes">
            <textarea
              className="inputAreaNotes"
              value={note}
              onChange={(evt) => notesChangedHandler(evt, evt.target.value)}
              minLength={1}
              maxLength={60}
              placeholder="write a note..."
              rows={6}
              cols={40}
            />
            <div className="textLength">{note.length}/60</div>
          </div>
          {showButtonGroup && (
            <div className="actionGroup">
              <Button type="primary" className="cancel" onClick={onClickCancel} ghost>
                Cancel
              </Button>
              <Button
                className="submit"
                type="primary"
                disabled={!updatedList.current.bigNumber || !successful}
                onClick={onAddSubmitNotes}
                // loading={submitButtonLoad}
                loading={safeGet(mappedProps, "notes.addedNotes.pending", false)}
              >
                {safeGet(mappedProps, "notes.addedNotes.pending", false) ? "" : "Submit"}
              </Button>
            </div>
          )}

          <div className="previousNotes">Previous notes</div>
          <ul className="notesList" ref={notesRef}>
            {notesList && renderNotesList()}
          </ul>
        </Container>
      </Layout>
    </>
  );
};

// TakeNotes.whyDidYouRender = true;
export default TakeNotes;
